import { mui } from '@local/finsera-core/src/libs';

import HelpContent from './help-content';
import * as types from '../types';

/**
 * Collapsable table component with expandable/collapsible sections
 *
 * @param title - Header title text or element
 * @param expanded - Whether the table is expanded
 * @param onChange - Callback for expansion state change
 * @param onClose - Callback for close action
 * @param children - Table content to be collapsible
 * @param headerContent - Custom header content
 * @param closedHeader - Content to show when collapsed
 * @param helpContent - Help content configuration
 * @param color - Color variant for header background
 *
 * @returns A collapsible table component with header and expandable content
 */

type CollapsableTableProps = {
  title: string | React.ReactElement;
  expanded?: boolean;
  onChange?: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  headerContent?: React.ReactNode;
  closedHeader?: React.ReactNode;
  helpContent?: types.helpItem.HelpItemContent;
  color?: string;
};

const CollapsableTable = ({
  expanded = false,
  onChange = () => undefined,
  title,
  onClose,
  children = null,
  headerContent,
  closedHeader,
  helpContent,
  color,
}: CollapsableTableProps) => {
  const theme = mui.styles.useTheme() as mui.core.Theme;

  return (
    <mui.core.Box
      sx={{
        position: 'relative',
        border: `1px solid ${(theme.palette as any).grayLight.main}`,
        marginBottom: '-1px',
        display: 'flex',
      }}
    >
      <mui.core.Box
        sx={{
          minWidth: '23vw',
          width: '23vw',
          alignItems: 'flex-start',
          display: 'flex',
          minHeight: '0',
          padding: '8px',
          cursor: 'pointer',
          borderRight: `1px solid ${(theme.palette as any).grayLight.main}`,
          background: color == 'error' ? (theme.palette as any).dangerLight.light : '#fff',
        }}
      >
        {headerContent}
        <mui.core.Box flex={1} onClick={onChange}>
          {helpContent ? (
            <HelpContent helpContent={helpContent}>
              <mui.core.Typography color={color} variant="body2" fontWeight="500">
                {title}
              </mui.core.Typography>
            </HelpContent>
          ) : (
            <mui.core.Typography color={color} variant="body2" fontWeight="500">
              {title}
            </mui.core.Typography>
          )}
        </mui.core.Box>

        <mui.core.Box flex="1" display="flex" justifyContent="flex-end">
          {onClose && (
            <mui.core.IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onClose();
              }}
            >
              <mui.icons.Close fontSize="small" sx={{ color: (theme.palette as any).text.secondary }} />
            </mui.core.IconButton>
          )}
          <mui.core.IconButton onClick={onChange} sx={{ textAlign: 'right' }} size="small">
            {expanded ? (
              <mui.icons.ExpandMore sx={{ color: (theme.palette as any).text.secondary }} />
            ) : (
              <mui.icons.ExpandLess sx={{ color: (theme.palette as any).text.secondary }} />
            )}
          </mui.core.IconButton>
        </mui.core.Box>
      </mui.core.Box>
      <mui.core.Box sx={{ flex: 1 }}>
        {expanded ? (
          children
        ) : (
          <mui.core.Box p={1} display="flex" sx={{ height: '100%' }}>
            {closedHeader}
          </mui.core.Box>
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default CollapsableTable;
